/**
 * Wave 3.85 / 3.86 — Manual Production Checklist section.
 *
 * Renders an 8-step operator-friendly flow with two parallel entry tracks:
 *
 *   CAD → CAM → Chamka → Ghat ─┐
 *                               ├─► Setting → QC → Delivery
 *   Raw Material ───────────────┘
 *
 * Ticking any step **cascades** its prerequisites (not everything before it).
 * Only Company Admin / Super Admin can un-tick, and only steps with no
 * downstream done step. Weights (CAD / CAM / Ghat) are captured via an
 * in-line dialog when the operator ticks manually — cascade ticks never
 * take a weight because those stages happened externally.
 *
 * Wave 3.86 absorbed the previous separate MilestonesSection into this
 * component so weights, notes, timelines and comment-callouts for
 * CAD/CAM/Chamka/Ghat now live inside each checklist row. Setting
 * Complete is also handled inline (requirement fulfilment gate).
 *
 * SLA colour coding (from backend `sla_status` field):
 *   completed  → green
 *   active     → blue
 *   at_risk    → amber
 *   delayed    → orange
 *   critical   → red (>= 2× SLA)
 *   not_started/blocked → slate grey
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  CheckCircle2, Circle as CircleIcon, ArrowRight, ShieldAlert, Loader2,
  Info, ChevronDown, ChevronRight, PauseCircle, Timer, AlertTriangle,
  XCircle, Cpu, Hourglass, Package, ShoppingCart, Truck, Coins,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { formatDateTime, formatGrams } from '@/lib/format';
import { useAuth } from '@/lib/auth';
import { CommentCalloutBadge, InlineCommentList } from '@/components/orderDetail/CommentCalloutBadge';
import { showGoldShortfallToast } from '@/lib/goldShortfall';
import { CadDeveloperCombobox } from '@/components/cad/CadDeveloperCombobox';

// Steps that capture a weight when ticked manually.
// Wave 3.87 — Chamka now also captures a weight/note.
const WEIGHT_STEPS = new Set(['cad', 'cam', 'chamka', 'ghat']);

// Steps that map onto the legacy "Ghat" comment topic (per Wave 3.65).
const GHAT_SUBTOPIC_BY_STEP = {
  cad: 'cad',
  cam: 'cam',
  chamka: 'chamka',
  ghat: 'ghat',
};

// Wave 4.3x — Setting comments are filed per raw-material head; QC comments
// per test head. These drive the per-scope comment callout badges + inline
// lists rendered on the Setting and QC checklist rows.
const SETTING_MATERIALS = [
  { id: 'diamond', label: 'Diamonds' },
  { id: 'color_stone', label: 'Colour Stones' },
  { id: 'pearl', label: 'Pearls' },
];
const QC_HEADS = [
  { id: 'gold_testing', label: 'Gold Testing' },
  { id: 'cvd_testing', label: 'CVD Testing' },
];

// Human-friendly hints for each step (shown on hover).
const STEP_HINTS = {
  cad: 'CAD confirmed — 3D design ready.',
  cam: 'CAM confirmed — machined wax/model ready.',
  chamka: 'Chamka issued — polishing/prep begins.',
  ghat: 'Ghat confirmed — base setting weight recorded.',
  raw_material: 'Raw material issued & every requirement fulfilled.',
  setting: 'Piece has been set — ready for QC.',
  qc: 'Quality Check passed.',
  delivery: 'Delivered to customer.',
};

// ---------------------------------------------------------------------------
// SLA-status → colour tokens
// ---------------------------------------------------------------------------
// The backend enriches each checklist entry with `sla_status`. Every visual
// affordance (step circle, label, detail-row background, status chip) reads
// from the same map so the palette stays consistent.
function statusStyle(status, isActive) {
  // Fallback for legacy responses (no sla_status)
  if (!status) {
    if (isActive) return statusStyle('active');
    return statusStyle('not_started');
  }
  switch (status) {
    case 'completed':
      return {
        circle:   'bg-emerald-500 text-white border-emerald-500',
        text:     'text-emerald-700',
        row:      'border-emerald-200 bg-emerald-50/40',
        chipCls:  'bg-emerald-50 text-emerald-700 border-emerald-200',
        chipText: 'Done',
        icon:     <CheckCircle2 size={12} />,
      };
    case 'active':
      return {
        circle:   'bg-white text-blue-700 border-blue-500 ring-2 ring-blue-200 animate-pulse-soft',
        text:     'text-blue-700',
        row:      'border-blue-200 bg-blue-50/40',
        chipCls:  'bg-blue-50 text-blue-700 border-blue-200',
        chipText: 'Active',
        icon:     <Timer size={12} />,
      };
    case 'waiting':
      // Wave 4.x — Ghat is waiting for gold to be issued. Calm amber (no
      // pulse) with an hourglass so it reads as "pending on gold", clearly
      // distinct from the pulsing amber "At risk" SLA state.
      return {
        circle:   'bg-white text-amber-600 border-amber-400 ring-2 ring-amber-100',
        text:     'text-amber-700',
        row:      'border-amber-200 bg-amber-50/50',
        chipCls:  'bg-amber-50 text-amber-700 border-amber-200',
        chipText: 'Waiting for gold',
        icon:     <Hourglass size={12} />,
      };
    case 'at_risk':
      return {
        circle:   'bg-white text-amber-700 border-amber-500 ring-2 ring-amber-200 animate-pulse-soft',
        text:     'text-amber-800',
        row:      'border-amber-200 bg-amber-50/40',
        chipCls:  'bg-amber-50 text-amber-800 border-amber-200',
        chipText: 'At risk',
        icon:     <Timer size={12} />,
      };
    case 'delayed':
      return {
        circle:   'bg-white text-orange-700 border-orange-500 ring-2 ring-orange-200 animate-pulse-soft',
        text:     'text-orange-700',
        row:      'border-orange-200 bg-orange-50/40',
        chipCls:  'bg-orange-50 text-orange-800 border-orange-200',
        chipText: 'Delayed',
        icon:     <AlertTriangle size={12} />,
      };
    case 'critical':
      return {
        circle:   'bg-white text-rose-700 border-rose-600 ring-2 ring-rose-200 animate-pulse-soft',
        text:     'text-rose-700',
        row:      'border-rose-300 bg-rose-50/50',
        chipCls:  'bg-rose-50 text-rose-800 border-rose-200',
        chipText: 'Critical',
        icon:     <AlertTriangle size={12} />,
      };
    case 'not_started':
    default:
      return {
        circle:   'bg-slate-100 text-slate-400 border-slate-200',
        text:     'text-slate-500',
        row:      'border-slate-200 bg-white',
        chipCls:  'bg-slate-100 text-slate-700 border-slate-200',
        chipText: 'Not started',
        icon:     <CircleIcon size={12} />,
      };
  }
}

function formatHoursShort(hours) {
  if (!hours || hours <= 0) return '—';
  if (hours < 24) return `${hours.toFixed(1)}h`;
  const d = Math.floor(hours / 24);
  const h = Math.round(hours - d * 24);
  if (h === 0) return `${d}d`;
  return `${d}d ${h}h`;
}

export function ProductionChecklistSection({ order, reload, onReceiveJewellery }) {
  const { user, isSuperAdmin, isCompanyAdmin, isSalesRole, hasMaterialAccess } = useAuth();
  const isAdmin = !!(isSuperAdmin || isCompanyAdmin);
  // Wave 4.xx — QC stage (Gold Testing / CVD Testing sub-checks + the QC
  // step itself) is editable by every role EXCEPT the view-only Sales roles.
  // All OTHER steps keep their prior rules (un-tick stays Admin-only).
  const canEditQc = !isSalesRole;
  const canUntickStep = (step) => (step === 'qc' ? canEditQc : isAdmin);
  const stage = (order.stage || '').trim();
  const isTerminal = stage === 'Cancelled' || stage === 'Completed';
  // Wave 3.99ac — Repair orders keep their own "Receive Repaired Piece"
  // action; standard jewellery orders launch the receive wizard straight
  // from the Delivery step (below).
  const isRepairOrder = order.order_type === 'repair' || !!order.customer_jewellery_item_id;

  const items = order.production_checklist || [];

  // Weight prompt dialog state.
  const [weightDlg, setWeightDlg] = useState({
    open: false, step: null, label: '', weight: '', note: '', busy: false, cadDeveloperId: '',
  });
  const [busyStep, setBusyStep] = useState(null);

  // Edit-weight dialog state (for already-done CAD/CAM/Ghat).
  const [editDlg, setEditDlg] = useState({
    open: false, step: null, label: '', weight: '', note: '', busy: false, cadDeveloperId: '',
  });

  // Wave 4.07 — CAD Developer master list for the (optional) CAD attribution
  // picker shown in the CAD weight / edit dialogs. Loaded best-effort: an
  // operator who lacks ``cad_developers.view`` simply sees an empty picker
  // (they can still tick CAD without a developer). Create is gated server-side.
  const [cadDevelopers, setCadDevelopers] = useState([]);
  const [cadDevLoading, setCadDevLoading] = useState(false);
  const loadCadDevelopers = useCallback(async () => {
    if (!order?.company_id) return;
    setCadDevLoading(true);
    try {
      const { data } = await api.get('/masters/cad-developers', { params: { company_id: order.company_id } });
      setCadDevelopers(Array.isArray(data) ? data : []);
    } catch {
      /* best-effort — picker stays usable, backend enforces create rights */
    } finally {
      setCadDevLoading(false);
    }
  }, [order?.company_id]);
  useEffect(() => { loadCadDevelopers(); }, [loadCadDevelopers]);

  const onCadDeveloperCreated = async (created) => {
    if (created?.id) {
      setCadDevelopers((prev) => (
        prev.some((d) => d.id === created.id)
          ? prev
          : [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      ));
    }
    loadCadDevelopers();
  };

  // Wave 3.94 — "CAD weight required" prompt. Opens whenever the user
  // tries to tick CAM (or a step that cascades CAM) without a CAD weight
  // on file. The backend also enforces this — the frontend is a UX
  // shortcut so the operator doesn't hit a red toast, they see a helpful
  // inline dialog instead.
  const [cadWeightDlg, setCadWeightDlg] = useState({
    open: false,
    // The CAM row the operator originally clicked — used to open the
    // CAM weight dialog automatically after the CAD weight is saved.
    followUpItem: null,
    cadWeight: '',
    busy: false,
  });

  // Wave 3.88 — QC Rejection dialog state.
  // Opens when the operator clicks "Reject" on the QC row (only visible
  // when QC is active or already done). The user picks the target step to
  // send it back to (ghat / raw_material / setting) and optionally supplies
  // a reason. The backend cascade-unticks every downstream done step and
  // pings the target step's stage-notify recipients.
  const [qcRejectDlg, setQcRejectDlg] = useState({
    open: false, target: 'ghat', reason: '', busy: false,
  });

  const openQcRejectDialog = () => {
    if (isTerminal) {
      toast.error(`Order is ${stage} — cannot reject QC.`);
      return;
    }
    setQcRejectDlg({ open: false, target: 'ghat', reason: '', busy: false });
    // Async pop so state resets before opening.
    setTimeout(() => {
      setQcRejectDlg({ open: true, target: 'ghat', reason: '', busy: false });
    }, 0);
  };

  const QC_REJECT_LABELS = {
    ghat: 'Ghat',
    raw_material: 'Raw Material Upload',
    setting: 'Setting',
  };

  const submitQcRejectDialog = async () => {
    if (qcRejectDlg.busy) return;
    const target = qcRejectDlg.target;
    if (!['ghat', 'raw_material', 'setting'].includes(target)) {
      toast.error('Pick a valid step to send it back to.');
      return;
    }
    setQcRejectDlg((s) => ({ ...s, busy: true }));
    try {
      await api.post(`/orders/${order.id}/checklist/qc/reject`, {
        send_back_to: target,
        reason: (qcRejectDlg.reason || '').trim(),
      });
      toast.success(`QC rejected — sent back to ${QC_REJECT_LABELS[target]}.`);
      setQcRejectDlg({ open: false, target: 'ghat', reason: '', busy: false });
      reload?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to reject QC'));
      setQcRejectDlg((s) => ({ ...s, busy: false }));
    }
  };

  // Wave 3.89 — QC sub-checks (Gold Testing + CVD Testing) state.
  // The backend attaches a `qc_subchecks` array on the order payload —
  // each entry is {name, label, required, done, done_at, done_by_email}.
  // When absent (legacy responses) we fall back to a synthetic pair with
  // `required=false` so nothing renders as needed.
  const qcSubchecks = order.qc_subchecks || [
    { name: 'cvd_testing', label: 'CVD Testing', required: false, done: false },
  ];
  const [busySubcheck, setBusySubcheck] = useState(null);

  const toggleQcSubcheck = async (name, currentlyDone) => {
    if (isTerminal) {
      toast.error(`Order is ${stage} — cannot modify QC sub-checks.`);
      return;
    }
    const action = currentlyDone ? 'untick' : 'tick';
    setBusySubcheck(name);
    try {
      await api.post(
        `/orders/${order.id}/checklist/qc/subcheck/${name}/${action}`,
      );
      const scLabel = (qcSubchecks.find((s) => s.name === name)?.label) || 'QC check';
      toast.success(`${scLabel} · ${currentlyDone ? 'un-ticked' : 'ticked'}.`);
      reload?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update QC sub-check'));
    } finally {
      setBusySubcheck(null);
    }
  };

  // Wave 4.13 — Ghat sub-checks (Gold Testing, moved from QC). The backend
  // attaches a `ghat_subchecks` array on the order payload — each entry is
  // {name, label, required, done, done_at, done_by_email, note, can_tick,
  // blocked_reason}. Gold Testing can only be ticked once gold is issued
  // (can_tick=false + a blocked_reason until then) and it GATES Ghat.
  const ghatSubchecks = order.ghat_subchecks || [
    { name: 'gold_testing', label: 'Gold Testing', required: true, done: false, can_tick: false, blocked_reason: '' },
  ];
  const [busyGhatSubcheck, setBusyGhatSubcheck] = useState(null);

  const toggleGhatSubcheck = async (name, currentlyDone) => {
    if (isTerminal) {
      toast.error(`Order is ${stage} — cannot modify Gold Testing.`);
      return;
    }
    const action = currentlyDone ? 'untick' : 'tick';
    setBusyGhatSubcheck(name);
    try {
      await api.post(
        `/orders/${order.id}/checklist/ghat/subcheck/${name}/${action}`,
      );
      const scLabel = (ghatSubchecks.find((s) => s.name === name)?.label) || 'Gold Testing';
      toast.success(`${scLabel} · ${currentlyDone ? 'un-ticked' : 'ticked'}.`);
      reload?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update Gold Testing'));
    } finally {
      setBusyGhatSubcheck(null);
    }
  };

  // ---------------------------------------------------------------------
  // Wave 4.10a — In-House CAD sub-workflows (Allot / WIP / Final)
  // ---------------------------------------------------------------------
  const [cadSubBusy, setCadSubBusy] = useState(null);  // `${name}:${action}`
  const [cadFinalDlg, setCadFinalDlg] = useState({ open: false, weight: '', busy: false });

  const performCadSub = async (name, action, body = {}) => {
    setCadSubBusy(`${name}:${action}`);
    try {
      const res = await api.post(
        `/orders/${order.id}/checklist/cad/subworkflow/${name}/${action}`,
        body,
      );
      toast.success(res.data?.message || `CAD ${name} ${action === 'tick' ? 'updated' : 'un-ticked'}.`);
      if (name === 'final' && action === 'tick') {
        try { showGoldShortfallToast(res?.data?.order?._gold_shortfall, order.id, reload); } catch { /* best-effort */ }
      }
      reload?.();
      return true;
    } catch (e) {
      toast.error(extractError(e, `Failed to ${action} CAD ${name}`));
      return false;
    } finally {
      setCadSubBusy(null);
    }
  };

  const handleCadAllot = (devId) => {
    if (!devId) return;
    performCadSub('allot', 'tick', { cad_developer_id: devId });
  };
  const handleCadWipToggle = (currentlyDone) => {
    performCadSub('wip', currentlyDone ? 'untick' : 'tick');
  };
  const submitCadFinalDialog = async () => {
    if (cadFinalDlg.busy) return;
    const wt = Number(String(cadFinalDlg.weight || '').trim());
    if (!Number.isFinite(wt) || wt <= 0) {
      toast.error('Enter a positive CAD weight in grams.');
      return;
    }
    setCadFinalDlg((s) => ({ ...s, busy: true }));
    const ok = await performCadSub('final', 'tick', { cad_weight_grams: wt });
    if (ok) setCadFinalDlg({ open: false, weight: '', busy: false });
    else setCadFinalDlg((s) => ({ ...s, busy: false }));
  };

  // ---------------------------------------------------------------------
  // Wave 4.10b — Raw Material buyer tracking (Order Raised / Material Collected)
  // ---------------------------------------------------------------------
  const [rmBusy, setRmBusy] = useState(null);  // `${kind}:${flag}`
  const performRmFlag = async (kind, flag, action) => {
    setRmBusy(`${kind}:${flag}`);
    try {
      const res = await api.post(
        `/orders/${order.id}/checklist/raw-material/${kind}/${flag}/${action}`,
      );
      toast.success(res.data?.message || 'Raw Material tracking updated.');
      reload?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update Raw Material tracking'));
    } finally {
      setRmBusy(null);
    }
  };

  // Wave 4.3y — Ghat GOLD buyer flag (single "Gold Order Raised" checkbox).
  // Mirrors ``performRmFlag``: posts to the gold-flag endpoint, toasts the
  // server message and soft-reloads the order so the refined 4-state status
  // (Pending Gold Order → Waiting for Gold → Ghat Confirmation) refreshes.
  const [goldBusy, setGoldBusy] = useState(null);  // flag id while in-flight
  const performGoldFlag = async (flag, action) => {
    setGoldBusy(flag);
    try {
      const res = await api.post(
        `/orders/${order.id}/checklist/ghat-gold/${flag}/${action}`,
      );
      toast.success(res.data?.message || 'Gold tracking updated.');
      reload?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update Gold tracking'));
    } finally {
      setGoldBusy(null);
    }
  };


  // Requirements fulfilment gate for Setting (mirrors backend).
  const reqs = order.requirements || [];
  const reqsTotal = reqs.length;
  const reqsCompleted = reqs.filter(
    (r) => ['complete', 'completed', 'done'].includes((r.status || '').toLowerCase()),
  ).length;
  const reqsFullyFulfilled = reqsTotal === 0 || reqsCompleted === reqsTotal;

  const openWeightDialog = (item) => {
    setWeightDlg({
      open: true, step: item.step, label: item.label,
      weight: item.weight_grams != null ? String(item.weight_grams) : '',
      note: '', busy: false,
      cadDeveloperId: item.step === 'cad' ? (order.cad_developer_id || '') : '',
    });
  };

  const openEditDialog = (item) => {
    setEditDlg({
      open: true, step: item.step, label: item.label,
      weight: item.weight_grams != null ? String(item.weight_grams) : '',
      note: '', busy: false,
      cadDeveloperId: item.step === 'cad' ? (order.cad_developer_id || '') : '',
    });
  };

  const performTick = async (step, extras = {}) => {
    setBusyStep(step);
    try {
      const body = {
        weight_grams: extras.weight_grams ?? null,
        note: extras.note || '',
      };
      // Wave 4.07 — Forward the optional CAD Developer attribution (only the
      // backend's ``cad`` step reads it; an empty string clears it).
      if (extras.cad_developer_id !== undefined) {
        body.cad_developer_id = extras.cad_developer_id;
      }
      const res = await api.post(
        `/orders/${order.id}/checklist/${step}/tick`,
        body,
      );
      if (res.data?.cascaded?.length) {
        toast.success(
          `Ticked ${step.toUpperCase().replace('_', ' ')} (auto-cascaded ${res.data.cascaded.map((s) => s.toUpperCase().replace('_', ' ')).join(', ')}).`,
        );
      } else {
        toast.success(`Ticked ${step.toUpperCase().replace('_', ' ')}.`);
      }
      // Wave 3.97 — When CAD was touched (directly or by cascade), the
      // backend attaches a ``_gold_shortfall`` summary so we can pop the
      // "Top-up / Reduce gold requirement" toast.
      if (step === 'cad' || (res?.data?.cascaded || []).includes('cad')) {
        try {
          showGoldShortfallToast(res?.data?._gold_shortfall, order.id, reload);
        } catch { /* best-effort */ }
      }
      reload?.();
      return true;
    } catch (e) {
      // Wave 3.94 — Backend sentinel: cascade attempts (e.g. ticking
      // Chamka while CAM is still not done) can also fail with
      // CAD_WEIGHT_REQUIRED. Instead of a red toast, escalate the
      // operator directly into the "Enter CAD Weight" dialog with a
      // follow-up that re-runs the original step once CAD is saved.
      const detail = e?.response?.data?.detail || '';
      if (typeof detail === 'string' && detail.startsWith('CAD_WEIGHT_REQUIRED')) {
        const original = items.find((i) => i.step === step) || { step, label: step.toUpperCase() };
        setCadWeightDlg({
          open: true,
          followUpItem: original,
          cadWeight: '',
          busy: false,
        });
        return false;
      }
      toast.error(extractError(e, `Failed to tick ${step}`));
      return false;
    } finally {
      setBusyStep(null);
    }
  };

  const handleTickClick = async (item) => {
    if (item.done) return;
    // Wave 4.xx — QC is view-only for Sales roles.
    if (item.step === 'qc' && isSalesRole) {
      toast.error('Your role has view-only access and cannot tick QC.');
      return;
    }
    // Wave 4.x — External-CAD orders: CAD/CAM/Chamka are not applicable.
    if (item.applicable === false) {
      toast.error("This order's CAD is external — CAD, CAM and Chamka are handled outside. Confirm Ghat directly once gold is issued.");
      return;
    }
    if (isTerminal) {
      toast.error(`Order is ${stage} — cannot modify checklist.`);
      return;
    }
    // Wave 3.87 — Give a helpful error immediately when the step is
    // blocked by a gate (grey circle) — no need to round-trip the server.
    // Backend still enforces the same rule on submit.
    if (!item.is_active) {
      // Wave 3.99ac — Prefer the backend's authoritative blocked reason so
      // we show the TRUE blocker (e.g. "Confirm Chamka first.") instead of
      // guessing "gold not issued" — which was wrong whenever gold was in
      // fact fulfilled and a DAG prereq like Chamka was the real hold-up.
      if (item.blocked_reason) {
        toast.error(item.blocked_reason);
        return;
      }
      if (item.step === 'ghat') {
        toast.error('Cannot confirm Ghat — gold has not been issued yet. Fulfil the Gold requirement first.');
        return;
      }
      if (item.step === 'raw_material') {
        toast.error('Cannot tick Raw Material — gold has not been issued yet. Fulfil the Gold requirement first.');
        return;
      }
      if (['setting', 'qc', 'delivery'].includes(item.step)) {
        toast.error(`${item.label} is not yet actionable — complete the earlier steps first.`);
        return;
      }
      // Generic fallback for any other step that isn't yet active.
      toast.error(`${item.label} isn't ready yet — earlier steps must be ticked first.`);
      return;
    }
    // Setting requires fulfilment gate.
    if (item.step === 'setting' && !reqsFullyFulfilled) {
      toast.error(`Cannot tick Setting yet — ${reqsCompleted}/${reqsTotal} requirements fulfilled.`);
      return;
    }
    // Wave 3.99ac — Delivery == the finished piece has come back from the
    // goldsmith. For a standard jewellery order (has goldsmith, not a
    // repair) ticking Delivery LAUNCHES the Receive Jewellery wizard
    // instead of ticking directly — the receipt is what completes the
    // order & marks Delivery done. Delivery only activates once the whole
    // production chain (through Quality Check) is done, so this naturally
    // blocks receiving until production is complete.
    if (item.step === 'delivery' && order.goldsmith_id && !isRepairOrder) {
      if (onReceiveJewellery) {
        onReceiveJewellery();
      } else {
        toast.error('Receive Jewellery is not available for this order.');
      }
      return;
    }
    // Wave 3.94 — CAM confirmation auto-generates a Gold requirement
    // from the order's CAD weight. If the CAD weight has never been
    // entered, prompt the operator to fill it in first (backend also
    // enforces this gate, but we shortcut the round-trip here).
    if (item.step === 'cam') {
      const cadItem = items.find((i) => i.step === 'cad');
      const cadWt = Number(cadItem?.weight_grams || 0);
      if (!cadWt || cadWt <= 0) {
        setCadWeightDlg({
          open: true,
          followUpItem: item,
          cadWeight: '',
          busy: false,
        });
        return;
      }
    }
    if (WEIGHT_STEPS.has(item.step)) {
      openWeightDialog(item);
      return;
    }
    await performTick(item.step);
  };

  // Wave 3.94 — Save CAD weight (either as an initial tick or as a
  // weight-update on an already-ticked CAD), then automatically re-open
  // the CAM weight dialog so the operator can complete their original
  // action in one flow.
  const submitCadWeightDialog = async () => {
    if (cadWeightDlg.busy) return;
    const cadWt = Number(String(cadWeightDlg.cadWeight || '').trim());
    if (!Number.isFinite(cadWt) || cadWt <= 0) {
      toast.error('Enter a positive CAD weight in grams');
      return;
    }
    setCadWeightDlg((s) => ({ ...s, busy: true }));
    const ok = await performTick('cad', { weight_grams: cadWt });
    if (!ok) {
      setCadWeightDlg((s) => ({ ...s, busy: false }));
      return;
    }
    const followUp = cadWeightDlg.followUpItem;
    setCadWeightDlg({ open: false, followUpItem: null, cadWeight: '', busy: false });
    // Re-open the CAM weight dialog so the user can finish the flow
    // they originally started.
    if (followUp) {
      // Small microtask delay so React commits the closed dialog first,
      // avoiding a jarring stacked-modal transition.
      setTimeout(() => openWeightDialog(followUp), 60);
    }
  };

  const submitWeightDialog = async () => {
    if (weightDlg.busy) return;
    const step = weightDlg.step;
    // Weight is OPTIONAL — user may skip if karigar didn't measure yet.
    const weightNum = weightDlg.weight.trim() === '' ? null : Number(weightDlg.weight);
    if (weightNum !== null && !Number.isFinite(weightNum)) {
      toast.error('Enter a numeric weight or leave blank');
      return;
    }
    setWeightDlg((s) => ({ ...s, busy: true }));
    const extras = { weight_grams: weightNum, note: weightDlg.note };
    if (step === 'cad') extras.cad_developer_id = weightDlg.cadDeveloperId || '';
    const ok = await performTick(step, extras);
    if (ok) {
      setWeightDlg({ open: false, step: null, label: '', weight: '', note: '', busy: false, cadDeveloperId: '' });
    } else {
      setWeightDlg((s) => ({ ...s, busy: false }));
    }
  };

  // Edit an already-done milestone's weight/note. Endpoint accepts the
  // same payload on already-ticked steps — the state machine treats it as
  // a weight_update.
  const submitEditDialog = async () => {
    if (editDlg.busy) return;
    const step = editDlg.step;
    const weightNum = editDlg.weight.trim() === '' ? null : Number(editDlg.weight);
    if (weightNum !== null && !Number.isFinite(weightNum)) {
      toast.error('Enter a numeric weight or leave blank');
      return;
    }
    setEditDlg((s) => ({ ...s, busy: true }));
    const extras = { weight_grams: weightNum, note: editDlg.note };
    if (step === 'cad') extras.cad_developer_id = editDlg.cadDeveloperId || '';
    const ok = await performTick(step, extras);
    if (ok) {
      setEditDlg({ open: false, step: null, label: '', weight: '', note: '', busy: false, cadDeveloperId: '' });
    } else {
      setEditDlg((s) => ({ ...s, busy: false }));
    }
  };

  const handleUntick = async (step) => {
    if (!canUntickStep(step)) {
      toast.error(step === 'qc'
        ? 'Your role has view-only access and cannot un-tick QC.'
        : 'Only Company Admin or Super Admin can un-tick.');
      return;
    }
    if (isTerminal) {
      toast.error(`Order is ${stage} — cannot modify checklist.`);
      return;
    }
    if (!confirm(`Un-tick ${step.toUpperCase().replace('_', ' ')}? Downstream state and material issues stay untouched.`)) return;
    setBusyStep(step);
    try {
      await api.post(`/orders/${order.id}/checklist/${step}/untick`);
      toast.success(`${step.toUpperCase().replace('_', ' ')} un-ticked.`);
      reload?.();
    } catch (e) {
      toast.error(extractError(e, `Failed to un-tick ${step}`));
    } finally {
      setBusyStep(null);
    }
  };

  const progress = useMemo(() => {
    const total = items.length || 8;
    const done = items.filter((i) => i.done).length;
    return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
  }, [items]);

  // Aggregate SLA badge for the whole section header.
  const worstStatus = useMemo(() => {
    const order_ = ['critical', 'delayed', 'at_risk', 'active', 'completed', 'not_started'];
    let best = 'not_started';
    for (const i of items) {
      if (order_.indexOf(i.sla_status || 'not_started') < order_.indexOf(best)) {
        best = i.sla_status || 'not_started';
      }
    }
    return best;
  }, [items]);
  const worstStyle = statusStyle(worstStatus);

  return (
    <Card className="p-5" data-testid="production-checklist-section">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-[var(--ink-navy)] flex items-center gap-2 flex-wrap">
            Production Checklist
            <CommentCalloutBadge
              filterKey="ghat"
              spec={{ topic: 'ghat' }}
              label="View all Ghat / milestone comments"
              testId="ghat-comments-callout"
            />
            {(worstStatus === 'delayed' || worstStatus === 'critical' || worstStatus === 'at_risk') && (
              <Badge className={`text-[10px] font-medium border ${worstStyle.chipCls}`}>
                {worstStyle.chipText}
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Tick each stage as it happens — prerequisites auto-cascade.
            Raw Material is independent of CAD/CAM/Chamka/Ghat; Setting needs both chains complete.
            Weights &amp; notes for CAD/CAM/Ghat are captured here and printed on the Job Card.
            {isAdmin ? '' : (isSalesRole ? '' : ' Only Admins can un-tick most steps (QC is open to your role).')}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="text-xs"
            data-testid="checklist-progress-badge"
          >
            {progress.done} of {progress.total} done · {progress.pct}%
          </Badge>
        </div>
      </div>

      {/* Visual step strip — coloured by sla_status. */}
      <div className="flex items-center overflow-x-auto pb-2 -mx-1 px-1" data-testid="checklist-strip">
        {items.map((item, idx) => {
          const isBusy = busyStep === item.step;
          const st = statusStyle(item.sla_status, item.is_active);
          const naStep = item.applicable === false;   // external-CAD CAD/CAM/Chamka
          return (
            <React.Fragment key={item.step}>
              <button
                type="button"
                onClick={() => handleTickClick(item)}
                disabled={item.done || isBusy || isTerminal || naStep || (item.step === 'qc' && isSalesRole)}
                title={naStep ? 'External CAD — not developed in-house' : ((item.step === 'qc' && isSalesRole) ? 'View only — QC is not editable for your role' : (STEP_HINTS[item.step] || ''))}
                className={`flex flex-col items-center gap-1 min-w-[72px] px-1.5 py-2 rounded-md transition ${
                  naStep
                    ? 'cursor-not-allowed opacity-45'
                    : item.done ? 'cursor-default' : isTerminal ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-slate-50'
                }`}
                data-testid={`checklist-step-${item.step}`}
              >
                <div
                  className={`h-9 w-9 rounded-full border-2 flex items-center justify-center text-[13px] font-semibold ${
                    naStep ? 'bg-slate-50 text-slate-300 border-dashed border-slate-300' : st.circle
                  }`}
                >
                  {isBusy ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : naStep ? (
                    <span className="text-slate-300">—</span>
                  ) : item.done ? (
                    <CheckCircle2 size={16} />
                  ) : (
                    <span>{idx + 1}</span>
                  )}
                </div>
                <div className={`text-[11px] font-medium text-center leading-tight ${naStep ? 'text-slate-400' : st.text}`}>
                  {item.label}
                </div>
                {naStep ? (
                  <div className="text-[9px] text-slate-400 uppercase tracking-wide">External</div>
                ) : item.is_active && item.elapsed_hours > 0 ? (
                  <div className="text-[9px] text-slate-500 font-mono">
                    {formatHoursShort(item.elapsed_hours)}
                    {item.sla_hours > 0 && (
                      <span className="text-slate-400"> / {formatHoursShort(item.sla_hours)}</span>
                    )}
                  </div>
                ) : null}
              </button>
              {idx < items.length - 1 && (
                <ArrowRight
                  size={14}
                  className={`mx-0.5 shrink-0 ${
                    items[idx + 1]?.done ? 'text-emerald-500' : 'text-slate-300'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Details rows */}
      <div className="mt-3 space-y-1.5" data-testid="checklist-detail-list">
        {items.map((item) => (
          <ChecklistDetailRow
            key={item.step}
            item={item}
            order={order}
            isAdmin={isAdmin}
            isSalesRole={isSalesRole}
            canUntick={canUntickStep(item.step)}
            isTerminal={isTerminal}
            busyStep={busyStep}
            onTick={() => handleTickClick(item)}
            onUntick={() => handleUntick(item.step)}
            onEditWeight={() => openEditDialog(item)}
            onQcReject={openQcRejectDialog}
            qcSubchecks={qcSubchecks}
            busySubcheck={busySubcheck}
            onToggleSubcheck={toggleQcSubcheck}
            ghatSubchecks={ghatSubchecks}
            busyGhatSubcheck={busyGhatSubcheck}
            onToggleGhatSubcheck={toggleGhatSubcheck}
            reqsCompleted={reqsCompleted}
            reqsTotal={reqsTotal}
            reqsFullyFulfilled={reqsFullyFulfilled}
            cadSubworkflows={order.cad_subworkflows || []}
            cadSubstage={order.cad_substage}
            cadSubBusy={cadSubBusy}
            cadDevelopers={cadDevelopers}
            cadDevLoading={cadDevLoading}
            onCadDeveloperCreated={onCadDeveloperCreated}
            onCadAllot={handleCadAllot}
            onCadWipToggle={handleCadWipToggle}
            onOpenCadFinal={() => setCadFinalDlg({ open: true, weight: order.cad_weight_grams != null ? String(order.cad_weight_grams) : '', busy: false })}
            canEditCad={!isSalesRole}
            rmSubmaterials={order.rm_submaterials || []}
            rmBusy={rmBusy}
            onRmToggle={performRmFlag}
            goldBusy={goldBusy}
            onGoldToggle={performGoldFlag}
            hasMaterialAccess={hasMaterialAccess}
          />
        ))}
      </div>

      {/* Info note */}
      <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-500">
        <Info size={12} className="mt-0.5 shrink-0" />
        <span>
          Ticking a step auto-ticks its prerequisites. CAD → CAM → Chamka → Ghat is one chain;
          Raw Material is a parallel track — Setting needs both chains complete. Uploading a
          Gold requirement auto-ticks CAD / CAM / Chamka (no weights) — because the karigar came
          with a ready spec. Raw Material auto-ticks when every requirement is fulfilled.
          Colour code: <span className="text-emerald-700">green = done</span>,{' '}
          <span className="text-blue-700">blue = active</span>,{' '}
          <span className="text-amber-800">amber = at risk</span>,{' '}
          <span className="text-orange-700">orange = delayed</span>,{' '}
          <span className="text-rose-700">red = critical (2× SLA)</span>.
        </span>
      </div>

      {/* Weight prompt dialog for CAD / CAM / Ghat manual ticks */}
      {/* Wave 3.94 — CAD Weight Required prompt (fires on CAM tick when
          the order has no CAD weight yet). The Gold requirement is
          auto-generated from this weight, so we insist on it here to
          avoid stalling the order downstream. */}
      <Dialog
        open={cadWeightDlg.open}
        onOpenChange={(o) => !o && !cadWeightDlg.busy && setCadWeightDlg({ open: false, followUpItem: null, cadWeight: '', busy: false })}
      >
        <DialogContent className="max-w-md" data-testid="checklist-cad-weight-required-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-600" />
              Enter CAD Weight First
            </DialogTitle>
            <DialogDescription>
              CAM confirmation needs the CAD weight so the Gold requirement can be
              auto-generated for this order. Enter the CAD weight in grams below —
              we&apos;ll save it and then take you straight to the CAM tick dialog.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">CAD Weight (grams)</Label>
              <Input
                type="number"
                step="0.001"
                min="0.001"
                autoFocus
                value={cadWeightDlg.cadWeight}
                onChange={(e) => setCadWeightDlg((s) => ({ ...s, cadWeight: e.target.value }))}
                placeholder="e.g. 25.000"
                data-testid="checklist-cad-weight-input"
                onKeyDown={(e) => { if (e.key === 'Enter') submitCadWeightDialog(); }}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Purity is inherited from the order&apos;s Jewellery Gold Purity.
                Existing Gold requirements are honoured — only the shortfall
                (if any) will be added.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCadWeightDlg({ open: false, followUpItem: null, cadWeight: '', busy: false })}
              disabled={cadWeightDlg.busy}
              data-testid="checklist-cad-weight-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitCadWeightDialog}
              disabled={cadWeightDlg.busy}
              data-testid="checklist-cad-weight-submit"
              className="luxury-button"
            >
              {cadWeightDlg.busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
              Save &amp; Continue to CAM
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={weightDlg.open} onOpenChange={(o) => !o && setWeightDlg((s) => ({ ...s, open: false }))}>
        <DialogContent className="max-w-md" data-testid="checklist-weight-dialog">
          <DialogHeader>
            <DialogTitle>Tick {weightDlg.label}</DialogTitle>
            <DialogDescription>
              Optionally capture the weight in grams and a hand-off note.
              Leave weight blank to tick without a weight — you can add it later.
              Both fields print on the Job Card.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {weightDlg.step === 'ghat' && order.ghat_gold_issued && (
              <div
                className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 space-y-1"
                data-testid="checklist-ghat-issued-info"
              >
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Gold issued</span>
                  <span className="font-medium text-[var(--ink-navy)]" data-testid="checklist-ghat-issued-value">
                    {Number(order.ghat_gold_issued.issued_equiv_grams || 0).toFixed(3)} g
                    {order.ghat_gold_issued.jewellery_purity_name ? ` @ ${order.ghat_gold_issued.jewellery_purity_name}` : ''}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">
                    Max Ghat weight allowed{Number(order.ghat_gold_issued.tolerance_grams || 0) > 0 ? ' (incl. tolerance)' : ''}
                  </span>
                  <span className="font-medium text-[var(--ink-navy)]" data-testid="checklist-ghat-max-allowed-value">
                    {Number(order.ghat_gold_issued.max_allowed_grams || 0).toFixed(3)} g
                  </span>
                </div>
                {Number(order.ghat_gold_issued.tolerance_grams || 0) > 0 && (
                  <p className="text-[11px] text-slate-400">
                    Includes a {Number(order.ghat_gold_issued.tolerance_grams).toFixed(3)} g tolerance for casting / scale variance.
                  </p>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500">Weight (grams)</Label>
              <Input
                type="number"
                step="0.001"
                value={weightDlg.weight}
                onChange={(e) => setWeightDlg((s) => ({ ...s, weight: e.target.value }))}
                placeholder="e.g. 12.345"
                data-testid="checklist-weight-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Note (optional)</Label>
              <Textarea
                value={weightDlg.note}
                onChange={(e) => setWeightDlg((s) => ({ ...s, note: e.target.value }))}
                placeholder="e.g. Matches CAD approval Rev-2"
                data-testid="checklist-weight-note"
              />
            </div>
            {weightDlg.step === 'cad' && (
              <div data-testid="checklist-weight-cad-developer">
                <Label className="text-xs text-slate-500">CAD Developer (optional)</Label>
                <CadDeveloperCombobox
                  developers={cadDevelopers}
                  value={weightDlg.cadDeveloperId}
                  onChange={(id) => setWeightDlg((s) => ({ ...s, cadDeveloperId: id }))}
                  companyId={order.company_id}
                  onDeveloperCreated={onCadDeveloperCreated}
                  loading={cadDevLoading}
                  testIdPrefix="checklist-weight-cad-developer-combobox"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Attribute who developed the CAD. Pick from the master or add a new one inline — this shows on the CAD Development report.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWeightDlg((s) => ({ ...s, open: false }))}
              disabled={weightDlg.busy}
              data-testid="checklist-weight-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitWeightDialog}
              disabled={weightDlg.busy}
              data-testid="checklist-weight-submit"
              className="luxury-button"
            >
              {weightDlg.busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
              Tick {weightDlg.label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit-weight dialog for already-done CAD / CAM / Ghat */}
      <Dialog open={editDlg.open} onOpenChange={(o) => !o && setEditDlg((s) => ({ ...s, open: false }))}>
        <DialogContent className="max-w-md" data-testid="checklist-edit-weight-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editDlg.label}</DialogTitle>
            <DialogDescription>
              Update the recorded weight and add a note. The change is logged on the
              milestone audit trail and reflects on the Job Card.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {editDlg.step === 'ghat' && order.ghat_gold_issued && (
              <div
                className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 space-y-1"
                data-testid="checklist-edit-ghat-issued-info"
              >
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Gold issued</span>
                  <span className="font-medium text-[var(--ink-navy)]" data-testid="checklist-edit-ghat-issued-value">
                    {Number(order.ghat_gold_issued.issued_equiv_grams || 0).toFixed(3)} g
                    {order.ghat_gold_issued.jewellery_purity_name ? ` @ ${order.ghat_gold_issued.jewellery_purity_name}` : ''}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">
                    Max Ghat weight allowed{Number(order.ghat_gold_issued.tolerance_grams || 0) > 0 ? ' (incl. tolerance)' : ''}
                  </span>
                  <span className="font-medium text-[var(--ink-navy)]" data-testid="checklist-edit-ghat-max-allowed-value">
                    {Number(order.ghat_gold_issued.max_allowed_grams || 0).toFixed(3)} g
                  </span>
                </div>
                {Number(order.ghat_gold_issued.tolerance_grams || 0) > 0 && (
                  <p className="text-[11px] text-slate-400">
                    Includes a {Number(order.ghat_gold_issued.tolerance_grams).toFixed(3)} g tolerance for casting / scale variance.
                  </p>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500">Weight (grams)</Label>
              <Input
                type="number"
                step="0.001"
                value={editDlg.weight}
                onChange={(e) => setEditDlg((s) => ({ ...s, weight: e.target.value }))}
                placeholder="e.g. 12.345"
                data-testid="checklist-edit-weight-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Note (optional)</Label>
              <Textarea
                value={editDlg.note}
                onChange={(e) => setEditDlg((s) => ({ ...s, note: e.target.value }))}
                placeholder="Any additional remark…"
                data-testid="checklist-edit-weight-note"
              />
            </div>
            {editDlg.step === 'cad' && (
              <div data-testid="checklist-edit-weight-cad-developer">
                <Label className="text-xs text-slate-500">CAD Developer (optional)</Label>
                <CadDeveloperCombobox
                  developers={cadDevelopers}
                  value={editDlg.cadDeveloperId}
                  onChange={(id) => setEditDlg((s) => ({ ...s, cadDeveloperId: id }))}
                  companyId={order.company_id}
                  onDeveloperCreated={onCadDeveloperCreated}
                  loading={cadDevLoading}
                  testIdPrefix="checklist-edit-cad-developer-combobox"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Update who developed the CAD. Clear it to remove the attribution.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDlg((s) => ({ ...s, open: false }))}
              disabled={editDlg.busy}
            >
              Cancel
            </Button>
            <Button
              onClick={submitEditDialog}
              disabled={editDlg.busy}
              className="luxury-button"
              data-testid="checklist-edit-weight-submit"
            >
              {editDlg.busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wave 3.88 — QC Rejection dialog. Cascades an un-tick from the
          chosen target step, resets its SLA clock, notifies the target
          step's stage-notify recipients, and posts a system comment. */}
      <Dialog
        open={qcRejectDlg.open}
        onOpenChange={(o) => !o && !qcRejectDlg.busy && setQcRejectDlg((s) => ({ ...s, open: false }))}
      >
        <DialogContent className="max-w-md" data-testid="qc-reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <XCircle size={18} /> Reject QC
            </DialogTitle>
            <DialogDescription>
              Send this piece back for rework. Every step from the target
              onwards will be un-ticked and its SLA clock will restart. The
              stage-notify recipients configured for the target step get a
              bell notification.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">Send back to</Label>
              <Select
                value={qcRejectDlg.target}
                onValueChange={(v) => setQcRejectDlg((s) => ({ ...s, target: v }))}
                disabled={qcRejectDlg.busy}
              >
                <SelectTrigger data-testid="qc-reject-target-select">
                  <SelectValue placeholder="Pick a step" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ghat" data-testid="qc-reject-target-ghat">Ghat</SelectItem>
                  <SelectItem value="raw_material" data-testid="qc-reject-target-raw-material">Raw Material Upload</SelectItem>
                  <SelectItem value="setting" data-testid="qc-reject-target-setting">Setting</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Everything from <b>{QC_REJECT_LABELS[qcRejectDlg.target] || '—'}</b> onwards will be reset.
              </p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Reason (optional)</Label>
              <Textarea
                value={qcRejectDlg.reason}
                onChange={(e) => setQcRejectDlg((s) => ({ ...s, reason: e.target.value }))}
                placeholder="e.g. Setting mis-aligned on prong 3 — needs re-do."
                rows={3}
                data-testid="qc-reject-reason-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                The reason is stored on the milestone log and posted as a system comment.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setQcRejectDlg((s) => ({ ...s, open: false }))}
              disabled={qcRejectDlg.busy}
              data-testid="qc-reject-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitQcRejectDialog}
              disabled={qcRejectDlg.busy}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="qc-reject-submit"
            >
              {qcRejectDlg.busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <XCircle size={14} className="mr-1.5" />}
              Reject &amp; send back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wave 4.10a — CAD Final weight dialog. Entering the CAD weight
          auto-confirms the main CAD step and raises the Gold requirement. */}
      <Dialog
        open={cadFinalDlg.open}
        onOpenChange={(o) => !o && !cadFinalDlg.busy && setCadFinalDlg({ open: false, weight: '', busy: false })}
      >
        <DialogContent className="max-w-md" data-testid="cad-final-weight-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cpu size={16} className="text-violet-600" />
              Finalise CAD — Enter Weight
            </DialogTitle>
            <DialogDescription>
              Enter the final CAD weight in grams. Saving it marks the CAD
              development <b>Final</b>, auto-confirms the CAD step, and raises the
              Gold requirement for this weight at the order&apos;s Gold Purity.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">CAD Weight (grams)</Label>
              <Input
                type="number"
                step="0.001"
                min="0.001"
                autoFocus
                value={cadFinalDlg.weight}
                onChange={(e) => setCadFinalDlg((s) => ({ ...s, weight: e.target.value }))}
                placeholder="e.g. 17.500"
                data-testid="cad-final-weight-input"
                onKeyDown={(e) => { if (e.key === 'Enter') submitCadFinalDialog(); }}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Purity is inherited from the order&apos;s Jewellery Gold Purity.
                Existing Gold requirements are honoured — only the shortfall
                (if any) is added.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCadFinalDlg({ open: false, weight: '', busy: false })}
              disabled={cadFinalDlg.busy}
              data-testid="cad-final-weight-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitCadFinalDialog}
              disabled={cadFinalDlg.busy}
              className="luxury-button"
              data-testid="cad-final-weight-submit"
            >
              {cadFinalDlg.busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
              Save &amp; Confirm CAD
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// ChecklistDetailRow
// ---------------------------------------------------------------------------
// Renders a single row under the visual strip. For CAD/CAM/Chamka/Ghat it
// exposes the weight (when applicable), inline note, timeline expander,
// and the comment callout badge. Colour matches SLA status.
function ChecklistDetailRow({
  item, order, isAdmin, isSalesRole, canUntick, isTerminal, busyStep,
  onTick, onUntick, onEditWeight, onQcReject,
  qcSubchecks, busySubcheck, onToggleSubcheck,
  ghatSubchecks, busyGhatSubcheck, onToggleGhatSubcheck,
  reqsCompleted, reqsTotal, reqsFullyFulfilled,
  cadSubworkflows, cadSubstage, cadSubBusy, cadDevelopers, cadDevLoading,
  onCadDeveloperCreated, onCadAllot, onCadWipToggle, onOpenCadFinal, canEditCad,
  rmSubmaterials, rmBusy, onRmToggle, goldBusy, onGoldToggle, hasMaterialAccess,
}) {
  const isBusy = busyStep === item.step;
  const st = statusStyle(item.sla_status, item.is_active);
  // Wave 4.3y — Ghat surfaces the refined 4-state gold buyer status on its
  // status chip (Pending Gold Order / Waiting for Gold) instead of the generic
  // "Waiting for gold". ghat_confirmation keeps the blue "Active" chip since
  // the step is ready to confirm once gold is issued.
  let chipText = st.chipText;
  let chipCls = st.chipCls;
  if (item.step === 'ghat') {
    if (item.gold_status === 'pending_gold_order') {
      chipText = 'Pending Gold Order';
      chipCls = 'bg-amber-50 text-amber-700 border-amber-200';
    } else if (item.gold_status === 'waiting_for_gold') {
      chipText = 'Waiting for Gold';
      chipCls = 'bg-amber-50 text-amber-700 border-amber-200';
    }
  }
  const naStep = item.applicable === false;   // external-CAD CAD/CAM/Chamka
  const showWeight = ['cad', 'cam', 'chamka', 'ghat'].includes(item.step);
  // Wave 3.99ac — On a standard jewellery order the Delivery step launches
  // the Receive Jewellery wizard, so label its button accordingly.
  const _isRepairOrder = order?.order_type === 'repair' || !!order?.customer_jewellery_item_id;
  const deliveryLaunchesReceive = item.step === 'delivery' && !!order?.goldsmith_id && !_isRepairOrder;
  const tickLabel = deliveryLaunchesReceive ? 'Receive Jewellery' : 'Tick';
  const subtopic = GHAT_SUBTOPIC_BY_STEP[item.step];
  const stampTxt = item.done_at ? formatDateTime(item.done_at) : '';
  const byTxt = item.done_by_email || '';

  const [expanded, setExpanded] = useState(false);
  // Wave 3.89 / 4.13 — Fold sub-check log entries into the right step row:
  //   * QC row → CVD Testing ticks (``qc_cvd_testing``).
  //   * Ghat row → Gold Testing ticks (``ghat_gold_testing``), since Gold
  //     Testing moved from QC to Ghat (Wave 4.13).
  // Legacy ``qc_gold_testing`` entries (pre-move) are kept on the QC row so
  // historical audit trails still render where they were originally recorded.
  const log = (order.milestones_log || []).filter((e) => {
    if (item.step === 'qc') {
      return e.kind === 'qc' || e.kind === 'qc_cvd_testing' || e.kind === 'qc_gold_testing';
    }
    if (item.step === 'ghat') {
      return e.kind === 'ghat' || e.kind === 'ghat_gold_testing';
    }
    return e.kind === item.step;
  });

  // Special-case Setting: block tick until reqs fully fulfilled.
  const settingBlocked = item.step === 'setting' && !item.done && !reqsFullyFulfilled;

  return (
    <div
      className={`rounded-md border px-3 py-2 text-sm ${naStep ? 'border-slate-200 bg-slate-50/70' : st.row}`}
      data-testid={`checklist-row-${item.step}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="flex items-center gap-2 min-w-[130px]">
          <span className={`inline-flex ${naStep ? 'text-slate-300' : st.text}`}>{st.icon}</span>
          <span className={`font-medium ${naStep ? 'text-slate-500' : 'text-[var(--ink-navy)]'}`}>{item.label}</span>
          {naStep && (
            <Badge
              variant="outline"
              className="text-[10px] border-slate-300 bg-slate-100 text-slate-500"
              data-testid={`checklist-external-cad-${item.step}`}
              title="CAD is developed externally — this step does not apply to this order"
            >
              <Cpu size={10} className="mr-0.5" /> External CAD
            </Badge>
          )}
          {!naStep && (item.done || item.is_active || item.awaiting_gold || item.sla_status === 'delayed' || item.sla_status === 'critical' || item.sla_status === 'at_risk') && (
            <Badge variant="outline" className={`text-[10px] border ${chipCls}`} data-testid={`checklist-chip-${item.step}`}>
              {chipText}
            </Badge>
          )}
          {item.auto_reason && (
            <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500">
              Auto
            </Badge>
          )}
          {/* Wave 3.91 — Stale-tick warning. The step is marked done but
              its extra gate (e.g. Ghat needs gold issued) no longer
              passes. Downstream steps won't activate off this tick — we
              surface it here so the operator understands why the flow
              looks stuck. */}
          {item.gate_invalid && (
            <Badge
              variant="outline"
              className="text-[10px] border-amber-300 bg-amber-50 text-amber-800"
              title={item.gate_invalid_reason || 'Prerequisite gate no longer passes.'}
              data-testid={`checklist-gate-warning-${item.step}`}
            >
              <AlertTriangle size={10} className="mr-0.5" /> Gate not met
            </Badge>
          )}
          {subtopic && (
            <CommentCalloutBadge
              filterKey={`ghat:${subtopic}`}
              spec={{ topic: 'ghat', subtopic_id: subtopic }}
              label={`View comments on ${item.label}`}
              testId={`checklist-comment-callout-${item.step}`}
              tone="amber"
              size="xs"
            />
          )}
          {item.step === 'qc' && (
            <CommentCalloutBadge
              filterKey="qc"
              spec={{ topic: 'qc' }}
              label="View all QC comments"
              testId="checklist-comment-callout-qc"
              tone="rose"
              size="xs"
              composerPreset={{ topic: 'qc' }}
            />
          )}
          {item.step === 'setting' && (
            <CommentCalloutBadge
              filterKey="setting"
              spec={{ topic: 'setting' }}
              label="View all setting comments"
              testId="checklist-comment-callout-setting"
              tone="navy"
              size="xs"
              composerPreset={{ topic: 'setting' }}
            />
          )}
        </div>
        <div className="flex-1 flex flex-wrap items-center gap-3 text-xs text-slate-600">
          {item.done && stampTxt && (
            <span data-testid={`checklist-row-${item.step}-stamp`}>
              <span className="text-slate-500">Ticked</span>{' '}
              <span className="font-mono">{stampTxt}</span>
              {byTxt ? <> · by <span className="text-slate-700">{byTxt}</span></> : ''}
            </span>
          )}
          {item.is_active && item.elapsed_hours > 0 && (
            <span className="text-slate-500 font-mono tabular-nums">
              {formatHoursShort(item.elapsed_hours)} elapsed
              {item.sla_hours > 0 && (
                <span className="text-slate-400"> / {formatHoursShort(item.sla_hours)} SLA</span>
              )}
            </span>
          )}
          {showWeight && item.weight_grams != null && item.done && (
            <span className="font-mono tabular-nums text-[var(--ink-navy)]" data-testid={`checklist-weight-${item.step}`}>
              <b>{formatGrams(item.weight_grams)} g</b>
            </span>
          )}
          {item.step === 'cad' && order?.cad_developer_name && (
            <span
              className="inline-flex items-center gap-1 text-[11px] text-indigo-700"
              data-testid="checklist-cad-developer-chip"
              title="CAD Developer"
            >
              <Cpu size={11} /> {order.cad_developer_name}
            </span>
          )}
          {item.note && (
            <span className="italic text-slate-500 truncate max-w-[300px]" title={item.note}>
              &ldquo;{item.note}&rdquo;
            </span>
          )}
          {settingBlocked && (
            <span className="inline-flex items-center gap-1 text-stone-600">
              <PauseCircle size={11} /> Waiting · {reqsCompleted}/{reqsTotal} requirements fulfilled
            </span>
          )}
          {item.awaiting_gold && (
            <span className="inline-flex items-center gap-1 text-amber-700" data-testid={`checklist-awaiting-gold-${item.step}`}>
              <Hourglass size={11} /> Waiting for gold to be issued — Ghat becomes active once gold is issued
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {log.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((s) => !s)}
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[var(--ink-navy)] px-2 py-1 rounded-md hover:bg-slate-50"
              data-testid={`checklist-timeline-toggle-${item.step}`}
              title={expanded ? 'Hide timeline' : 'Show timeline'}
            >
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              Timeline
              <Badge className="ml-1 bg-slate-100 text-slate-700 border-slate-200 text-[10px] font-mono">{log.length}</Badge>
            </button>
          )}
          {item.done && showWeight && !isTerminal && (
            <Button
              size="sm"
              variant="outline"
              disabled={isBusy}
              onClick={onEditWeight}
              data-testid={`checklist-edit-weight-${item.step}-button`}
            >
              Edit weight
            </Button>
          )}
          {!item.done && !naStep && (
            <Button
              size="sm"
              variant="outline"
              disabled={isBusy || isTerminal || settingBlocked || item.awaiting_gold || (item.step === 'qc' && isSalesRole)}
              onClick={onTick}
              data-testid={`checklist-tick-${item.step}-button`}
              title={(item.step === 'qc' && isSalesRole)
                ? 'View only — QC is not editable for your role'
                : (settingBlocked
                  ? 'Fulfil all requirements first'
                  : (item.awaiting_gold
                    ? 'Waiting for gold to be issued — Ghat can be ticked once gold is issued'
                    : undefined))}
            >
              {isBusy ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
              {tickLabel}
            </Button>
          )}
          {naStep && (
            <span className="text-[11px] text-slate-400 italic" data-testid={`checklist-external-note-${item.step}`}>
              Handled externally
            </span>
          )}
          {/* Wave 3.88 — QC Reject button: visible on the QC row when QC is
              currently active OR already done (and order isn't terminal).
              Opens a dialog to pick where to send the piece back to. */}
          {item.step === 'qc' && (item.is_active || item.done) && !isTerminal && !isSalesRole && (
            <Button
              size="sm"
              variant="outline"
              disabled={isBusy}
              onClick={onQcReject}
              className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
              data-testid="checklist-qc-reject-button"
              title="Reject QC and send back for rework"
            >
              {isBusy ? (
                <Loader2 size={12} className="mr-1 animate-spin" />
              ) : (
                <XCircle size={12} className="mr-1" />
              )}
              Reject
            </Button>
          )}
          {item.done && item.is_last_ticked && canUntick && !isTerminal && (
            <Button
              size="sm"
              variant="ghost"
              className="text-rose-600 hover:text-rose-700"
              disabled={isBusy}
              onClick={onUntick}
              data-testid={`checklist-untick-${item.step}-button`}
            >
              {isBusy ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
              Un-tick
            </Button>
          )}
          {item.done && !item.is_last_ticked && (
            <span title="Un-tick downstream steps first">
              <ShieldAlert size={14} className="text-slate-300" />
            </span>
          )}
        </div>
      </div>

      {/* Wave 3.89 — QC sub-checkboxes: Gold Testing + CVD Testing.
          Rendered inline under the QC row. Each shows a small checkbox,
          label, timestamp/user (when ticked), and greys out if the
          sub-check is not required for this order (e.g. no diamond
          requirement means CVD Testing is optional). */}
      {item.step === 'qc' && Array.isArray(qcSubchecks) && qcSubchecks.length > 0 && (
        <div
          className="mt-2 pt-2 border-t border-dashed border-[var(--divider)] flex flex-wrap gap-x-6 gap-y-2"
          data-testid="qc-subchecks-row"
        >
          {qcSubchecks.map((sc) => {
            const isSubBusy = busySubcheck === sc.name;
            const notRequired = !sc.required;
            // Notes:
            //  * Anyone with edit rights can toggle (backend gates).
            //  * Once QC itself is done, the whole row is display-only.
            //  * Non-required sub-check: show greyed out with a hint.
            const disabled = isTerminal || item.done || notRequired || isSubBusy || isSalesRole;
            return (
              <div
                key={sc.name}
                className={`flex items-center gap-2 text-xs ${notRequired ? 'opacity-50' : ''}`}
                data-testid={`qc-subcheck-${sc.name}`}
              >
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={!!sc.done}
                  disabled={disabled}
                  onClick={() => onToggleSubcheck?.(sc.name, !!sc.done)}
                  className={`h-4 w-4 rounded border flex items-center justify-center transition ${
                    sc.done
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : 'bg-white border-slate-300 hover:border-emerald-400'
                  } ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                  data-testid={`qc-subcheck-toggle-${sc.name}`}
                  title={
                    notRequired
                      ? `${sc.label} is not required for this order`
                      : sc.done
                        ? `Un-tick ${sc.label}`
                        : `Mark ${sc.label} complete`
                  }
                >
                  {isSubBusy ? (
                    <Loader2 size={10} className="animate-spin" />
                  ) : sc.done ? (
                    <CheckCircle2 size={12} />
                  ) : null}
                </button>
                <span className={`font-medium ${sc.done ? 'text-emerald-700' : 'text-[var(--ink-navy)]'}`}>
                  {sc.label}
                </span>
                {notRequired && (
                  <Badge variant="outline" className="text-[9px] border-slate-300 text-slate-500">
                    N/A
                  </Badge>
                )}
                {sc.done && sc.done_at && (
                  <span className="text-[10px] text-slate-500 font-mono">
                    {formatDateTime(sc.done_at)}
                    {sc.done_by_email ? (
                      <>
                        {' '}· by <span className="text-slate-700">{sc.done_by_email}</span>
                      </>
                    ) : ''}
                  </span>
                )}
                <CommentCalloutBadge
                  filterKey={`qc:${sc.name}`}
                  spec={{ topic: 'qc', subtopic_id: sc.name }}
                  label={`View QC comments for ${sc.label}`}
                  testId={`checklist-comment-callout-qc-${sc.name}`}
                  tone="rose"
                  size="xs"
                  composerPreset={{ topic: 'qc', subtopicId: sc.name }}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Wave 4.3x — Setting comment callouts, one per raw material head
          (Diamonds / Colour Stones / Pearls). Each badge shows the count and
          toggles an inline list below. */}
      {item.step === 'setting' && (
        <div
          className="mt-2 pt-2 border-t border-dashed border-[var(--divider)] flex flex-wrap items-center gap-x-5 gap-y-2"
          data-testid="setting-comments-row"
        >
          <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Comments</span>
          {SETTING_MATERIALS.map((m) => (
            <div key={m.id} className="flex items-center gap-1.5 text-xs" data-testid={`setting-comment-head-${m.id}`}>
              <span className="text-[var(--ink-navy)]">{m.label}</span>
              <CommentCalloutBadge
                filterKey={`setting:${m.id}`}
                spec={{ topic: 'setting', subtopic_id: m.id }}
                label={`View setting comments for ${m.label}`}
                testId={`checklist-comment-callout-setting-${m.id}`}
                tone="navy"
                size="xs"
                composerPreset={{ topic: 'setting', subtopicId: m.id }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Wave 4.10a — In-House CAD sub-workflow panel (Allot / WIP / Final).
          Only for in-house CAD orders, rendered under the CAD row. */}
      {item.step === 'cad' && item.applicable !== false && Array.isArray(cadSubworkflows) && cadSubworkflows.length > 0 && (
        <CadSubworkflowPanel
          order={order}
          subworkflows={cadSubworkflows}
          substage={cadSubstage}
          busy={cadSubBusy}
          isTerminal={isTerminal}
          canEdit={canEditCad && !isTerminal}
          cadDevelopers={cadDevelopers}
          cadDevLoading={cadDevLoading}
          onDeveloperCreated={onCadDeveloperCreated}
          onAllot={onCadAllot}
          onWipToggle={onCadWipToggle}
          onOpenFinal={onOpenCadFinal}
        />
      )}

      {/* Wave 4.10b — Raw Material buyer tracking panel (Order Raised /
          Material Collected per side material). Visibility gated by the
          user's material-access scope. */}
      {item.step === 'raw_material' && Array.isArray(rmSubmaterials) && (
        <RmBuyerPanel
          order={order}
          submaterials={rmSubmaterials}
          busy={rmBusy}
          isTerminal={isTerminal}
          hasMaterialAccess={hasMaterialAccess}
          onToggle={onRmToggle}
        />
      )}

      {/* Wave 4.3y — Ghat GOLD buyer tracking panel. A single "Gold Order
          Raised" checkbox (with who + when) drives the refined 4-state gold
          pipeline. Visibility gated by the user's Gold material-access scope. */}
      {item.step === 'ghat' && (
        <GhatGoldPanel
          goldStatus={item.gold_status}
          goldFlags={item.gold_flags}
          busy={goldBusy}
          isTerminal={isTerminal}
          isSalesRole={isSalesRole}
          hasMaterialAccess={hasMaterialAccess}
          onToggle={onGoldToggle}
        />
      )}

      {/* Wave 4.13 — Ghat sub-check: Gold Testing (moved from QC). Rendered
          under the Ghat row. Gold Testing can only be ticked once gold is
          issued (can_tick=false + a blocked_reason until then) and it GATES
          Ghat confirmation. Once Ghat is confirmed the row is display-only
          (un-tick Ghat first to change). Sales roles are view-only. */}
      {item.step === 'ghat' && Array.isArray(ghatSubchecks) && ghatSubchecks.length > 0 && (
        <div
          className="mt-2 pt-2 border-t border-dashed border-[var(--divider)] flex flex-wrap gap-x-6 gap-y-2"
          data-testid="ghat-subchecks-row"
        >
          {ghatSubchecks.map((sc) => {
            const isSubBusy = busyGhatSubcheck === sc.name;
            const blocked = !sc.done && sc.can_tick === false;
            const disabled = isTerminal || item.done || isSalesRole || isSubBusy || blocked;
            return (
              <div
                key={sc.name}
                className="flex items-center gap-2 text-xs"
                data-testid={`ghat-subcheck-${sc.name}`}
              >
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={!!sc.done}
                  disabled={disabled}
                  onClick={() => onToggleGhatSubcheck?.(sc.name, !!sc.done)}
                  className={`h-4 w-4 rounded border flex items-center justify-center transition ${
                    sc.done
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : 'bg-white border-slate-300 hover:border-emerald-400'
                  } ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                  data-testid={`ghat-subcheck-toggle-${sc.name}`}
                  title={
                    blocked
                      ? (sc.blocked_reason || `${sc.label} is locked until gold is issued`)
                      : item.done
                        ? `${sc.label} — un-tick Ghat first to change`
                        : sc.done
                          ? `Un-tick ${sc.label}`
                          : `Mark ${sc.label} complete`
                  }
                >
                  {isSubBusy ? (
                    <Loader2 size={10} className="animate-spin" />
                  ) : sc.done ? (
                    <CheckCircle2 size={12} />
                  ) : null}
                </button>
                <span className={`font-medium ${sc.done ? 'text-emerald-700' : 'text-[var(--ink-navy)]'}`}>
                  {sc.label}
                </span>
                <Badge variant="outline" className="text-[9px] border-amber-300 bg-amber-50 text-amber-700">
                  Required before Ghat
                </Badge>
                {sc.done && sc.done_at && (
                  <span className="text-[10px] text-slate-500 font-mono">
                    {formatDateTime(sc.done_at)}
                    {sc.done_by_email ? (
                      <>
                        {' '}· by <span className="text-slate-700">{sc.done_by_email}</span>
                      </>
                    ) : ''}
                  </span>
                )}
                {sc.done && sc.note && (
                  <span
                    className="text-[10px] text-slate-500 italic"
                    data-testid={`ghat-subcheck-note-${sc.name}`}
                  >
                    &ldquo;{sc.note}&rdquo;
                  </span>
                )}
                {blocked && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] text-amber-700"
                    data-testid={`ghat-subcheck-blocked-${sc.name}`}
                  >
                    <Hourglass size={10} /> {sc.blocked_reason || 'Waiting for gold to be issued'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {expanded && log.length > 0 && (
        <div className="mt-3 pt-3 border-t border-dashed border-[var(--divider)]">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2 font-semibold">Timeline</div>
          <div className="relative pl-4">
            <div className="absolute left-1.5 top-1 bottom-1 w-px bg-slate-200" aria-hidden="true" />
            <ul className="space-y-2">
              {[...log].reverse().map((e, i) => (
                <li key={i} className="relative">
                  <div
                    className={`absolute -left-3 top-1.5 h-2 w-2 rounded-full ring-2 ring-white ${
                      e.action === 'unticked' ? 'bg-rose-500' : e.action === 'weight_updated' ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                    aria-hidden="true"
                  />
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[12px]">
                    <span className="font-mono text-slate-700">{formatDateTime(e.at)}</span>
                    <Badge
                      className={`text-[9px] uppercase ${
                        e.action === 'unticked'
                          ? 'bg-rose-50 text-rose-700 border border-rose-200'
                          : e.action === 'weight_updated'
                            ? 'bg-amber-50 text-amber-800 border border-amber-200'
                            : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      }`}
                    >
                      {(e.action || '').replace('_', ' ')}
                    </Badge>
                    {e.weight_grams != null && (
                      <span className="font-mono tabular-nums text-[var(--ink-navy)]">
                        {formatGrams(e.weight_grams)} g
                      </span>
                    )}
                    <span className="text-slate-500">
                      by <span className="text-slate-700">{e.user_email}</span>
                    </span>
                    {e.auto_reason && (
                      <span className="text-slate-400 italic">
                        ({e.auto_reason.replace(/_/g, ' ')})
                      </span>
                    )}
                    {e.note && <span className="text-slate-500 italic">— {e.note}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Per-row inline comment list — only shown when the callout is toggled. */}
      {subtopic && (
        <InlineCommentList
          filterKey={`ghat:${subtopic}`}
          spec={{ topic: 'ghat', subtopic_id: subtopic }}
          emptyHint={`No comments on ${item.label} yet. Use the composer with Topic = Ghat › ${item.label} to add one.`}
          testIdPrefix={`checklist-${item.step}-comment`}
        />
      )}
      {/* Wave 4.3x — Setting inline comment lists: overall roll-up + one per
          material head. Each carries a composer preset so "Add Comment" posts
          straight into that scope. */}
      {item.step === 'setting' && (
        <>
          <InlineCommentList
            filterKey="setting"
            spec={{ topic: 'setting' }}
            emptyHint="No setting comments yet. Use the composer with Topic = Setting."
            testIdPrefix="checklist-setting-comment"
            composerPreset={{ topic: 'setting' }}
          />
          {SETTING_MATERIALS.map((m) => (
            <InlineCommentList
              key={m.id}
              filterKey={`setting:${m.id}`}
              spec={{ topic: 'setting', subtopic_id: m.id }}
              emptyHint={`No setting comments for ${m.label} yet. Use the composer with Topic = Setting › ${m.label}.`}
              testIdPrefix={`checklist-setting-${m.id}-comment`}
              composerPreset={{ topic: 'setting', subtopicId: m.id }}
            />
          ))}
        </>
      )}
      {/* Wave 4.3x — QC inline comment lists: overall + one per test head. */}
      {item.step === 'qc' && (
        <>
          <InlineCommentList
            filterKey="qc"
            spec={{ topic: 'qc' }}
            emptyHint="No QC comments yet. Use the composer with Topic = QC."
            testIdPrefix="checklist-qc-comment"
            composerPreset={{ topic: 'qc' }}
          />
          {QC_HEADS.map((h) => (
            <InlineCommentList
              key={h.id}
              filterKey={`qc:${h.id}`}
              spec={{ topic: 'qc', subtopic_id: h.id }}
              emptyHint={`No ${h.label} comments yet. Use the composer with Topic = QC › ${h.label}.`}
              testIdPrefix={`checklist-qc-${h.id}-comment`}
              composerPreset={{ topic: 'qc', subtopicId: h.id }}
            />
          ))}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wave 4.10a — CadSubworkflowPanel
// ---------------------------------------------------------------------------
// Optional In-House CAD development tracker rendered under the CAD row.
// Three steps: Allot Developer → Work In Progress → Final (CAD weight).
// Entering the final weight auto-confirms the main CAD step (handled server
// side) and raises the Gold requirement.
const CAD_SUBSTAGE_LABELS = { not_allotted: 'Not Allotted', wip: 'WIP', final: 'Final' };

function SubStepDot({ done }) {
  return (
    <span
      className={`inline-flex h-4 w-4 items-center justify-center rounded-full border ${
        done ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-slate-300 text-slate-300'
      }`}
    >
      {done ? <CheckCircle2 size={11} /> : <CircleIcon size={9} />}
    </span>
  );
}

export function CadSubworkflowPanel({
  order, subworkflows, substage, busy, isTerminal, canEdit,
  cadDevelopers, cadDevLoading, onDeveloperCreated, onAllot, onWipToggle, onOpenFinal,
}) {
  const cadDone = !!order?.cad_done;
  const byName = (n) => subworkflows.find((s) => s.name === n) || {};
  const allot = byName('allot');
  const wip = byName('wip');
  const final = byName('final');
  const subLabel = CAD_SUBSTAGE_LABELS[substage] || null;
  const subTone = substage === 'final'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : substage === 'wip'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-slate-100 text-slate-600 border-slate-200';

  return (
    <div
      className="mt-2 pt-2 border-t border-dashed border-[var(--divider)]"
      data-testid="cad-subworkflow-panel"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold inline-flex items-center gap-1">
          <Cpu size={11} /> In-House CAD Workflow
        </span>
        <span className="text-[10px] text-slate-400 normal-case">optional</span>
        {subLabel && (
          <Badge variant="outline" className={`text-[10px] border ${subTone}`} data-testid="cad-substage-badge">
            {subLabel}
          </Badge>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        {/* 1 · Allot Developer */}
        <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2" data-testid="cad-sub-allot">
          <div className="flex items-center gap-1.5 mb-1.5">
            <SubStepDot done={!!allot.done} />
            <span className="text-xs font-medium text-[var(--ink-navy)]">1 · Allot Developer</span>
          </div>
          {cadDone || !canEdit ? (
            <div className="text-[11px] text-slate-600" data-testid="cad-sub-allot-readonly">
              {order?.cad_developer_name || <span className="text-slate-400">Not allotted</span>}
            </div>
          ) : (
            <CadDeveloperCombobox
              developers={cadDevelopers}
              value={allot.cad_developer_id || ''}
              onChange={(id) => id && onAllot(id)}
              companyId={order.company_id}
              onDeveloperCreated={onDeveloperCreated}
              loading={cadDevLoading}
              testIdPrefix="cad-sub-allot-combobox"
            />
          )}
          {allot.done && allot.done_at && (
            <div className="text-[10px] text-slate-400 mt-1 font-mono">{formatDateTime(allot.done_at)}</div>
          )}
        </div>

        {/* 2 · Work In Progress */}
        <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2" data-testid="cad-sub-wip">
          <div className="flex items-center gap-1.5 mb-1.5">
            <SubStepDot done={!!wip.done || cadDone} />
            <span className="text-xs font-medium text-[var(--ink-navy)]">2 · Work In Progress</span>
          </div>
          {cadDone ? (
            <div className="text-[11px] text-emerald-700">Completed</div>
          ) : (
            <Button
              size="sm"
              variant={wip.done ? 'outline' : 'outline'}
              className={wip.done ? 'h-7 text-[11px] border-emerald-300 text-emerald-700' : 'h-7 text-[11px]'}
              disabled={!canEdit || isTerminal || busy === 'wip:tick' || busy === 'wip:untick'}
              onClick={() => onWipToggle(!!wip.done)}
              data-testid="cad-sub-wip-toggle"
            >
              {(busy === 'wip:tick' || busy === 'wip:untick')
                ? <Loader2 size={11} className="mr-1 animate-spin" />
                : (wip.done ? <CheckCircle2 size={11} className="mr-1" /> : null)}
              {wip.done ? 'In Progress' : 'Mark WIP'}
            </Button>
          )}
          {wip.done && wip.done_at && (
            <div className="text-[10px] text-slate-400 mt-1 font-mono">{formatDateTime(wip.done_at)}</div>
          )}
        </div>

        {/* 3 · Final (CAD weight) */}
        <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2" data-testid="cad-sub-final">
          <div className="flex items-center gap-1.5 mb-1.5">
            <SubStepDot done={!!final.done || cadDone} />
            <span className="text-xs font-medium text-[var(--ink-navy)]">3 · Final (CAD Weight)</span>
          </div>
          {(final.done || cadDone) ? (
            <div className="text-[11px] text-emerald-700 font-mono" data-testid="cad-sub-final-weight">
              {order?.cad_weight_grams != null ? `${formatGrams(order.cad_weight_grams)} g` : 'Confirmed'}
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px]"
              disabled={!canEdit || isTerminal || busy === 'final:tick'}
              onClick={onOpenFinal}
              data-testid="cad-sub-final-open"
            >
              {busy === 'final:tick' ? <Loader2 size={11} className="mr-1 animate-spin" /> : null}
              Enter CAD Weight
            </Button>
          )}
          {final.done && final.done_at && (
            <div className="text-[10px] text-slate-400 mt-1 font-mono">{formatDateTime(final.done_at)}</div>
          )}
        </div>
      </div>
      <p className="text-[10px] text-slate-400 mt-1.5">
        Entering the final CAD weight auto-confirms the CAD step and raises the Gold requirement.
      </p>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Wave 4.10b — RmBuyerPanel
// ---------------------------------------------------------------------------
// Per-side-material procurement tracker rendered under the Raw Material row.
// Each visible material shows two checkboxes: Order Raised + Material
// Collected. Visibility is gated by the caller's material-access scope.
const RM_FLAG_ICON = { order_raised: ShoppingCart, material_collected: Truck };

function RmBuyerPanel({ order, submaterials, busy, isTerminal, hasMaterialAccess, onToggle }) {
  const visible = (submaterials || []).filter(
    (m) => m.has_requirement && (typeof hasMaterialAccess === 'function' ? hasMaterialAccess(m.kind, 'see') : true),
  );
  if (!visible.length) return null;

  return (
    <div
      className="mt-2 pt-2 border-t border-dashed border-[var(--divider)]"
      data-testid="rm-buyer-panel"
    >
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2 font-semibold inline-flex items-center gap-1">
        <Package size={11} /> Material Procurement Tracking
      </div>
      <div className="space-y-1.5">
        {visible.map((m) => {
          const issued = !!m.issued;
          const orderRaisedDone = (m.flags.find((f) => f.flag === 'order_raised') || {}).done;
          return (
            <div
              key={m.kind}
              className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5"
              data-testid={`rm-buyer-${m.kind}`}
            >
              <span className="text-xs font-medium text-[var(--ink-navy)] min-w-[92px]">{m.label}</span>
              {issued && (
                <Badge variant="outline" className="text-[9px] border-emerald-300 bg-emerald-50 text-emerald-700" data-testid={`rm-buyer-${m.kind}-issued`}>
                  <CheckCircle2 size={9} className="mr-0.5" /> Issued
                </Badge>
              )}
              {m.flags.map((f) => {
                const FlagIcon = RM_FLAG_ICON[f.flag] || CircleIcon;
                const key = `${m.kind}:${f.flag}`;
                const isBusy = busy === key;
                // Material Collected can only be ticked after Order Raised.
                const lockedByOrder = f.flag === 'material_collected' && !orderRaisedDone && !f.done;
                const disabled = isTerminal || issued || isBusy || lockedByOrder;
                return (
                  <div key={f.flag} className="flex items-center gap-1.5 text-xs" data-testid={`rm-flag-${m.kind}-${f.flag}`}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={!!f.done}
                      disabled={disabled}
                      onClick={() => onToggle(m.kind, f.flag, f.done ? 'untick' : 'tick')}
                      className={`h-4 w-4 rounded border flex items-center justify-center transition ${
                        f.done
                          ? 'bg-sky-500 border-sky-500 text-white'
                          : 'bg-white border-slate-300 hover:border-sky-400'
                      } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                      data-testid={`rm-flag-toggle-${m.kind}-${f.flag}`}
                      title={lockedByOrder ? 'Tick Order Raised first' : (issued ? 'Material already issued' : (f.done ? `Un-tick ${f.label}` : `Mark ${f.label}`))}
                    >
                      {isBusy ? <Loader2 size={10} className="animate-spin" /> : f.done ? <CheckCircle2 size={12} /> : null}
                    </button>
                    <span className={`inline-flex items-center gap-1 font-medium ${f.done ? 'text-sky-700' : 'text-slate-600'}`}>
                      <FlagIcon size={12} /> {f.label}
                    </span>
                    {f.done && f.done_at && (
                      <span className="text-[10px] text-slate-400 font-mono">{formatDateTime(f.done_at)}</span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Wave 4.3y — GhatGoldPanel
// ---------------------------------------------------------------------------
// Ghat gold buyer tracker rendered under the Ghat row. A SINGLE "Gold Order
// Raised" checkbox (with who + when) drives the refined 4-state gold pipeline:
//   No Requirement → Pending Gold Order → Waiting for Gold → Ghat Confirmation
// Only renders once a Gold requirement exists and the viewer has Gold access.
// Sales roles are view-only (checkbox disabled).
const GOLD_STATUS_META = {
  no_requirement:    { label: 'No Requirement',    cls: 'border-slate-300 bg-slate-50 text-slate-600' },
  pending_gold_order:{ label: 'Pending Gold Order', cls: 'border-amber-300 bg-amber-50 text-amber-700' },
  waiting_for_gold:  { label: 'Waiting for Gold',   cls: 'border-amber-300 bg-amber-50 text-amber-700' },
  ghat_confirmation: { label: 'Ghat Confirmation',  cls: 'border-emerald-300 bg-emerald-50 text-emerald-700' },
};

function GhatGoldPanel({ goldStatus, goldFlags, busy, isTerminal, isSalesRole, hasMaterialAccess, onToggle }) {
  const canSeeGold = typeof hasMaterialAccess === 'function' ? hasMaterialAccess('gold', 'see') : true;
  if (!canSeeGold) return null;
  // Nothing to track until a Gold requirement exists.
  if (goldStatus === 'no_requirement' || !goldStatus) return null;

  const orderRaised = (Array.isArray(goldFlags) ? goldFlags : []).find((f) => f.flag === 'order_raised') || null;
  const done = !!(orderRaised && orderRaised.done);
  const confirmed = goldStatus === 'ghat_confirmation';
  const meta = GOLD_STATUS_META[goldStatus] || GOLD_STATUS_META.pending_gold_order;
  const isBusy = busy === 'order_raised';
  // Once gold is issued (Ghat Confirmation) the order is locked; sales is
  // view-only; a terminal order can't be edited.
  const disabled = isTerminal || confirmed || isSalesRole || isBusy;

  return (
    <div
      className="mt-2 pt-2 border-t border-dashed border-[var(--divider)]"
      data-testid="ghat-gold-panel"
    >
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2 font-semibold inline-flex items-center gap-1">
        <Coins size={11} /> Gold Procurement Tracking
        <Badge variant="outline" className={`ml-1 text-[9px] border ${meta.cls}`} data-testid="ghat-gold-status-badge">
          {meta.label}
        </Badge>
      </div>
      <div
        className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5"
        data-testid="ghat-gold-order-raised-row"
      >
        {confirmed && (
          <Badge variant="outline" className="text-[9px] border-emerald-300 bg-emerald-50 text-emerald-700" data-testid="ghat-gold-issued-badge">
            <CheckCircle2 size={9} className="mr-0.5" /> Gold Issued
          </Badge>
        )}
        <div className="flex items-center gap-1.5 text-xs" data-testid="ghat-gold-flag-order_raised">
          <button
            type="button"
            role="checkbox"
            aria-checked={done}
            disabled={disabled}
            onClick={() => onToggle('order_raised', done ? 'untick' : 'tick')}
            className={`h-4 w-4 rounded border flex items-center justify-center transition ${
              done
                ? 'bg-amber-500 border-amber-500 text-white'
                : 'bg-white border-slate-300 hover:border-amber-400'
            } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            data-testid="ghat-gold-toggle-order_raised"
            title={
              isSalesRole ? 'Sales roles have view-only access'
                : confirmed ? 'Gold already issued'
                : (done ? 'Un-tick Gold Order Raised' : 'Mark Gold Order Raised')
            }
          >
            {isBusy ? <Loader2 size={10} className="animate-spin" /> : done ? <CheckCircle2 size={12} /> : null}
          </button>
          <span className={`inline-flex items-center gap-1 font-medium ${done ? 'text-amber-700' : 'text-slate-600'}`}>
            <ShoppingCart size={12} /> Gold Order Raised
          </span>
          {done && orderRaised?.done_at && (
            <span className="text-[10px] text-slate-400 font-mono" data-testid="ghat-gold-order_raised-stamp">
              {formatDateTime(orderRaised.done_at)}
              {orderRaised.done_by_email ? ` · ${orderRaised.done_by_email}` : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}



export default ProductionChecklistSection;